import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCountry } from "../contexts/CountryContext";
import { CheckCircle, Phone, Mail } from "lucide-react";
import { PasswordInput } from "../components/shared/PasswordInput";
import { pixelTrack } from "../lib/metaPixel";

const SignupPage = () => {
  const { signUp, resendVerification, isAuthenticated, isRecoveryMode, recoveryError } = useAuth();
  const { country } = useCountry();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: searchParams.get('role') || 'customer'
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const confirmRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  useEffect(() => {
    if (isAuthenticated && !isRecoveryMode && !recoveryError) {
      navigate('/');
    }
  }, [isAuthenticated, isRecoveryMode, recoveryError, navigate]);

  // Fire Meta Pixel "Lead" the first time the user picks the Provider role
  // (anywhere in the funnel — landing on /signup?role=provider directly, OR
  // clicking the "Offer Services" button on the signup form).
  useEffect(() => {
    if (formData.role === 'provider' && !window.__pixelProviderLeadFired) {
      window.__pixelProviderLeadFired = true;
      pixelTrack('Lead', { content_name: 'Provider Signup', content_category: 'provider_intent' });
    }
  }, [formData.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPasswordError('');

    const focusConfirm = () => {
      const node = confirmRef.current;
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = node.querySelector('input');
        if (input) setTimeout(() => input.focus(), 250);
      }
    };

    if (formData.password.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      focusConfirm();
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match.');
      focusConfirm();
      return;
    }

    if (!acceptedTerms) {
      setError(formData.role === 'provider'
        ? 'You must agree to the Terms of Service, Privacy Policy, and Provider Agreement.'
        : 'You must agree to the Terms of Service and Privacy Policy.');
      return;
    }

    setLoading(true);

    const { data, error } = await signUp({
      email: formData.email,
      password: formData.password,
      fullName: formData.fullName,
      role: formData.role,
      phone: formData.phone,
      acceptedTerms: true
    });

    if (error) {
      setError(error.message || 'Failed to create account');
    } else {
      setSignupSuccess(true);
      setRegisteredEmail(formData.email);
      // Pixel: account created (separate event for provider vs customer)
      pixelTrack('CompleteRegistration', {
        content_name: formData.role === 'provider' ? 'Provider Signup' : 'Customer Signup',
        content_category: formData.role,
        status: 'pending_email_verification',
      });
    }

    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    const { error } = await resendVerification(registeredEmail);
    if (error) {
      setResendMsg(error.message || 'Failed to resend. Please try again.');
    } else {
      setResendMsg('Verification email sent! Check your inbox.');
    }
    setResending(false);
  };

  if (signupSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-container" style={{ textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', margin: '0 auto 20px', background: 'rgba(34,197,94,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Mail size={32} style={{ color: 'var(--success)' }} />
          </div>
          <h1 data-testid="signup-success-title">Account Created</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', maxWidth: '400px', margin: '12px auto 24px', lineHeight: 1.6 }} data-testid="signup-success-message">
            We've sent a verification link to <strong>{registeredEmail}</strong>. Please check your inbox and click the link to activate your HandyHub account.
          </p>
          {resendMsg && (
            <div style={{ padding: '10px 16px', borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '0.875rem', background: resendMsg.includes('sent') ? '#ECFDF5' : '#FEF2F2', color: resendMsg.includes('sent') ? '#065F46' : '#DC2626' }} data-testid="resend-message">
              {resendMsg}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <Link to="/login" className="btn-primary btn-full" data-testid="back-to-login-btn">Back to Login</Link>
            <button onClick={handleResend} className="btn-outline btn-full" disabled={resending} data-testid="resend-verification-btn">
              {resending ? 'Sending...' : 'Resend Verification Email'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Create Account</h1>
        <p>Join HandyHub today</p>

        {error && <div className="error-message" data-testid="signup-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form" data-testid="signup-form">
          <div className="form-group">
            <label>I want to</label>
            <div className="role-selector">
              <button
                type="button"
                className={`role-btn ${formData.role === 'customer' ? 'active' : ''}`}
                onClick={() => { setFormData({...formData, role: 'customer'}); setAcceptedTerms(false); }}
                data-testid="role-customer"
              >
                Find Services
              </button>
              <button
                type="button"
                className={`role-btn ${formData.role === 'provider' ? 'active' : ''}`}
                onClick={() => { setFormData({...formData, role: 'provider'}); setAcceptedTerms(false); }}
                data-testid="role-provider"
              >
                Offer Services
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
              placeholder="Enter your full name"
              required
              data-testid="fullname-input"
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="Enter your email"
              required
              data-testid="email-input"
            />
          </div>
          <div className="form-group">
            <label>Phone (Optional)</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder={country.phonePlaceholder}
              data-testid="phone-input"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordInput
              value={formData.password}
              onChange={(e) => { setFormData({...formData, password: e.target.value}); if (passwordError) setPasswordError(''); }}
              placeholder="Create a password"
              required
              data-testid="password-input"
            />
          </div>
          <div className="form-group" ref={confirmRef}>
            <label>Confirm Password</label>
            <PasswordInput
              value={formData.confirmPassword}
              onChange={(e) => { setFormData({...formData, confirmPassword: e.target.value}); if (passwordError) setPasswordError(''); }}
              placeholder="Confirm your password"
              required
              aria-invalid={passwordError ? 'true' : 'false'}
              aria-describedby="password-error"
              data-testid="confirm-password-input"
            />
            {passwordError && (
              <p
                id="password-error"
                role="alert"
                data-testid="password-error"
                style={{ margin: '8px 0 0', color: '#dc2626', fontSize: '0.8125rem', fontWeight: 500 }}
              >
                {passwordError}
              </p>
            )}
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '4px' }}>
            <input
              type="checkbox"
              id="accept-terms"
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                e.target.setCustomValidity('');
              }}
              onInvalid={(e) => e.target.setCustomValidity(
                formData.role === 'provider'
                  ? 'Please read and accept the Terms of Service, Privacy Policy, and Provider Agreement by ticking the box.'
                  : 'Please read and accept the Terms of Service and Privacy Policy by ticking the box.'
              )}
              required
              data-testid="accept-terms-checkbox"
              style={{ marginTop: '3px', cursor: 'pointer', width: 'auto' }}
            />
            <label htmlFor="accept-terms" style={{ fontSize: '0.8125rem', color: 'var(--text-dark)', lineHeight: 1.5, cursor: 'pointer' }}>
              I agree to the{' '}
              <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }} data-testid="terms-link">Terms of Service</Link>
              {formData.role === 'provider' ? <>, </> : <> and </>}
              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }} data-testid="privacy-link">Privacy Policy</Link>
              {formData.role === 'provider' && (
                <>
                  {', and '}
                  <Link to="/provider-agreement" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }} data-testid="provider-agreement-link">Provider Agreement</Link>
                </>
              )}
              .
            </label>
          </div>
          <button 
            type="submit" 
            className="btn-primary btn-full"
            disabled={loading}
            data-testid="signup-submit"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="signup-trust-text" data-testid="signup-trust-text">
          {formData.role === 'provider' ? (
            <>
              <p data-testid="signup-trial-line"><CheckCircle size={14} /> {country.trialLabelShort} free trial for new providers</p>
              <p data-testid="signup-pricing-line"><CheckCircle size={14} /> Then {country.currencyPrefix}49 + {country.gstLabel}/month or {country.currencyPrefix}{country.yearlyPrice} + {country.gstLabel}/year</p>
              <p><CheckCircle size={14} /> Reach local customers in your area</p>
            </>
          ) : (
            <>
              <p><CheckCircle size={14} /> Free to join</p>
              <p><CheckCircle size={14} /> Get quotes from local professionals</p>
              <p><CheckCircle size={14} /> Compare trusted providers near you</p>
            </>
          )}
        </div>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};


export default SignupPage;
